import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaShieldAlt, FaArrowLeft, FaCopy, FaCheck } from "react-icons/fa";
import { authService } from "../../services/api";
import { PRODUCT_NAME } from "../../config/brand";

/**
 * The second step of a candidate-app sign-in by a teacher who holds exam
 * personas (W3.1; backend authController.candidateSignIn). The password — or
 * the LMS — said who this is; when the institution requires staff two-step
 * sign-in, the server answered a pending token instead of a session
 * (services/staffMfa.js). This screen finishes it, exactly as the console's
 * (apps/console/src/components/auth/StaffMfa.jsx) does:
 *
 *   enrol   — first time: the QR to scan (or the key to type), then the first
 *             code; the ten recovery codes are shown ONCE before going on.
 *   verify  — the code from the app, or a recovery code.
 *
 * Plain students never see it. `onDone(session)` gets what the sign-in
 * answers (plus `to` from the LMS); `onCancel` goes back to the sign-in. Its
 * words are the exam namespace's (mfa.*): /launch on exam day leads here.
 */
const inputClass =
  "w-full border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500";

// The server's refusals (backend services/staffMfa.js), by code.
const ERRORS = {
  MFA_CODE_INVALID: "mfa.error.codeInvalid",
  MFA_RECOVERY_INVALID: "mfa.error.recoveryInvalid",
  MFA_TOO_MANY_ATTEMPTS: "mfa.error.tooMany",
  MFA_SESSION_EXPIRED: "mfa.error.expired",
  MFA_NOT_STARTED: "mfa.error.notStarted",
};
/** What to say for a refusal: an mfa.error.* key, and the minutes to wait (too many tries). */
const refusal = (err, fallback) => {
  const data = err?.response?.data || {};
  return { key: ERRORS[data.code] || fallback, minutes: Math.max(1, Math.ceil(Number(data.retryAfterSeconds || 60) / 60)) };
};

export default function StaffMfa({ challenge, onDone, onCancel }) {
  const { t } = useTranslation("exam");
  const { mfaToken, mfaStage, user } = challenge;
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null); // { key: an mfa.error.* key, minutes }
  const [saved, setSaved] = useState(null); // { session, recoveryCodes } after enrolment
  const [copied, setCopied] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (mfaStage !== "enrol" || started.current) return;
    started.current = true;
    setBusy(true);
    authService
      .mfaEnrol(mfaToken)
      .then(({ data }) => setSetup(data))
      .catch((err) => setError(refusal(err, "mfa.error.setupFailed")))
      .finally(() => setBusy(false));
  }, [mfaStage, mfaToken]);

  const submit = async (e) => {
    e.preventDefault();
    const typed = code.trim();
    if (!typed) return;
    setBusy(true);
    setError(null);
    try {
      if (mfaStage === "enrol") {
        const { data } = await authService.mfaConfirm(mfaToken, typed.replace(/\s/g, ""));
        const { recoveryCodes, ...session } = data;
        setSaved({ session, recoveryCodes: recoveryCodes || [] });
      } else {
        const body = useRecovery ? { recoveryCode: typed } : { code: typed.replace(/\s/g, "") };
        const { data } = await authService.mfaVerify(mfaToken, body);
        onDone(data);
      }
    } catch (err) {
      setError(refusal(err, "mfa.error.failed"));
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    try {
      await navigator.clipboard.writeText(saved.recoveryCodes.join("\n"));
      setCopied(true);
    } catch {
      /* the codes are on screen to write down */
    }
  };

  const who = `${user?.name ? `${user.name} · ` : ""}${PRODUCT_NAME}`;
  const header = (title) => (
    <div className="flex items-center gap-3">
      <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600">
        <FaShieldAlt aria-hidden="true" />
      </span>
      <div>
        <h1 className="text-lg font-bold text-slate-900">{title}</h1>
        <p className="text-xs text-slate-500">{who}</p>
      </div>
    </div>
  );
  const errorStrip = error ? (
    <div className="text-sm rounded-lg bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2" role="alert" data-testid="mfa-error">
      {t(error.key, { minutes: error.minutes })}
    </div>
  ) : null;
  const primary =
    "inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50";

  // Enrolled: the recovery codes, once.
  if (saved) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-4" data-testid="mfa-recovery-codes">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          {header(t("mfa.recovery.title"))}
          <p className="text-sm text-slate-600">{t("mfa.recovery.body")}</p>
          <ol className="grid grid-cols-2 gap-2 font-mono text-sm text-slate-800 bg-slate-50 rounded-xl p-3" data-testid="mfa-recovery-list">
            {saved.recoveryCodes.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ol>
          <div className="flex items-center justify-between gap-2">
            <button type="button" onClick={copyCodes} className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-indigo-600">
              {copied ? <FaCheck aria-hidden="true" /> : <FaCopy aria-hidden="true" />} {copied ? t("mfa.recovery.copied") : t("mfa.recovery.copy")}
            </button>
            <button type="button" className={primary} onClick={() => onDone(saved.session)} data-testid="mfa-recovery-done">
              {t("mfa.recovery.done")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const enrol = mfaStage === "enrol";
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-4" data-testid={enrol ? "mfa-enrol" : "mfa-challenge"}>
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
        {header(enrol ? t("mfa.enrol.title") : t("mfa.verify.title"))}
        {enrol ? (
          <>
            <p className="text-sm text-slate-600">{t("mfa.enrol.body")}</p>
            {setup ? (
              <div className="flex flex-col items-center gap-2">
                <img src={setup.qr} alt={t("mfa.enrol.qrAlt")} width={180} height={180} data-testid="mfa-qr" />
                <p className="text-xs text-slate-500">{t("mfa.enrol.cannotScan")}</p>
                <code className="font-mono text-sm text-slate-800 bg-slate-50 rounded-lg px-2 py-1 select-all break-all" data-testid="mfa-manual-key">
                  {setup.manualKey}
                </code>
              </div>
            ) : (
              !error && <p className="text-sm text-slate-500">{t("mfa.enrol.preparing")}</p>
            )}
          </>
        ) : (
          <p className="text-sm text-slate-600">{useRecovery ? t("mfa.verify.useRecovery") : t("mfa.verify.useCode")}</p>
        )}
        <div>
          <label htmlFor="mfa-code" className="block text-sm font-semibold text-slate-700 mb-1">
            {useRecovery ? t("mfa.label.recovery") : enrol ? t("mfa.label.enrolCode") : t("mfa.label.code")}
          </label>
          <input
            id="mfa-code"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
              setError(null);
            }}
            inputMode={useRecovery ? "text" : "numeric"}
            autoComplete="one-time-code"
            autoFocus
            maxLength={useRecovery ? 16 : 7}
            className={`${inputClass} font-mono tracking-widest`}
            data-testid="mfa-code"
          />
        </div>
        {errorStrip}
        <div className="flex items-center justify-between gap-2 pt-1">
          <button type="button" onClick={onCancel} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-rose-600">
            <FaArrowLeft aria-hidden="true" /> {t("mfa.back")}
          </button>
          <button type="submit" className={primary} disabled={busy || !code.trim() || (enrol && !setup)} data-testid="mfa-submit">
            {enrol ? t("mfa.confirm") : t("mfa.verifyButton")}
          </button>
        </div>
        {!enrol && (
          <button
            type="button"
            onClick={() => {
              setUseRecovery((v) => !v);
              setCode("");
              setError(null);
            }}
            className="text-xs text-indigo-600 hover:underline"
            data-testid="mfa-use-recovery"
          >
            {useRecovery ? t("mfa.switch.toApp") : t("mfa.switch.toRecovery")}
          </button>
        )}
      </form>
    </div>
  );
}
